import Support from './global/Support'
import { clamp } from './Maths'

export default class SmoothScrolling
{
    constructor(params)
    {
        this.initialized = false

        this.defaultParams =
        {
            section: undefined,
            sectionWidth: 0,
            sectionLimit: 0,
            canStart: true,
            ease: 0,
            firefoxMult: 30,
            mouseMult: 0.2,
            onTick: null,
            events:
            [
                // In every case, touchevents for mobile will be available
                // 'vertical-scroll'
                // 'horizontal-scroll',
                // 'drag',
                // 'keyboard'
            ],
            eventKeyboard:
            {
                step: 100
            }
        }

        this.params = Object.assign(this.defaultParams, params)

        this.params.sectionWidth = this.params.sectionWidth === 0 ? this.params.section.offsetWidth : this.params.sectionWidth

        if (this.params.events.length === 0)
        {
            this.params.events.push('vertical-scroll')
        }

        if (!this.params.section)
        {
            console.error('ERROR: section is undefined in smooth-scrolling.js')
            return
        }

        this.canStart = this.params.canStart

        this.scrollOffset = 0
        this.scrollOffsetlast = 0

        this.gesture =
        {
            moving: false,
            start:
            {
                x: 0,
                y: 0
            },
            previous:
            {
                x: 0,
                y: 0
            },
            x: 0,
            y: 0,
            position: {
                x: 0,
                y: 0
            },
            speed: {
                x: 0,
                y: 0
            },
            delta: {
                x: 0,
                y: 0
            }
        }

        this.scroll = {
            auto: false,
            from: 0,
            to: 0
        }

        this.duration = {
            start: null,
            current: null
        }

        this.support = new Support()
    }

    init()
    {
        this.addEvents()

        window.requestAnimationFrame(this._onTick.bind(this))
    }

    destroy()
    {
        this.removeEvents()
    }

    addEvents()
    {
        if (this.support.touch)
        {
            this.params.section.addEventListener('touchstart', this._onTouchstart.bind(this))
            this.params.section.addEventListener('touchmove', this._onTouchmove.bind(this))
            this.params.section.addEventListener('touchend', this._onTouchend.bind(this))
        }
        else if (this.params.events.includes('drag'))
        {
            this.params.section.classList.add('is-can-grab')
            this.params.section.addEventListener('mousedown', this._onMousedown.bind(this))
            this.params.section.addEventListener('mousemove', this._onMousemove.bind(this))
            this.params.section.addEventListener('mouseup', this._onMouseup.bind(this))
        }

        if (this.support.hasWheelEvent)
        {
            document.addEventListener('wheel', this._onWheel.bind(this))
        }

        if (this.support.hasMouseWheelEvent)
        {
            document.addEventListener('mousewheel', this._onMouseWheel.bind(this))
        }

        if (this.support.hasKeyDown
            && this.params.events.includes('keyboard'))
        {
            document.addEventListener('keydown', this._onKeydown.bind(this))
        }

        this.initialized = true
    }

    removeEvents()
    {
        if (this.support.touch)
        {
            this.params.section.removeEventListener('touchstart', this._onTouchstart.bind(this))
            this.params.section.removeEventListener('touchmove', this._onTouchmove.bind(this))
            this.params.section.removeEventListener('touchend', this._onTouchend.bind(this))
        }
        else
        {
            this.params.section.removeEventListener('mousedown', this._onMousedown.bind(this))
            this.params.section.removeEventListener('mousemove', this._onMousemove.bind(this))
            this.params.section.removeEventListener('mouseup', this._onMouseup.bind(this))
        }

        document.removeEventListener('keydown', this._onKeydown.bind(this))
        document.removeEventListener('mousewheel', this._onMouseWheel.bind(this))
        document.removeEventListener('wheel', this._onWheel.bind(this))
    }

    _onTick()
    {
        window.requestAnimationFrame(this._onTick.bind(this))

        if (!this.scroll.auto)
        {
            if (this.gesture.moving)
            {
                const deltaX = this.gesture.x - this.gesture.previous.x
                const deltaY = this.gesture.y - this.gesture.previous.y

                this.gesture.previous.x = this.gesture.x
                this.gesture.previous.y = this.gesture.y

                this.gesture.speed.x = deltaX * 1
                this.gesture.speed.y = deltaY * 1
            }
            else
            {
                this.gesture.previous.x = this.gesture.x
                this.gesture.previous.y = this.gesture.y

                this.gesture.speed.x *= 0.95
                this.gesture.speed.y *= 0.95

                if (Math.abs(this.gesture.speed.x) < 0.01)
                {
                    this.gesture.speed.x = 0
                }
                if (Math.abs(this.gesture.speed.y) < 0.01)
                {
                    this.gesture.speed.y = 0
                }
            }

            const speed = this.gesture.speed.x !== 0 ? this.gesture.speed.x : this.gesture.speed.y !== 0 ? this.gesture.speed.y : 0

            this.gesture.position.x += speed

            if (this.gesture.position.x > 0)
            {
                this.gesture.speed.x = 0
                this.gesture.speed.y = 0
                this.gesture.position.x = 0
            }
            else if (this.gesture.position.x < this.params.sectionLimit)
            {
                this.gesture.speed.x = 0
                this.gesture.speed.y = 0
                this.gesture.position.x = this.params.sectionLimit
            }
        }
        else
        {
            this.duration.current = new Date()

            let time = clamp((this.duration.current - this.duration.start) / 1000 /* 1000 ms */, 0, 1)
            const easeInOut = 1 + (--time) * time * time * time * time

            this.gesture.position.x = this.scroll.from + (this.scroll.to - this.scroll.from) * easeInOut

            if (time === 0)
            {
                this.scroll.auto = null
                this.scroll.from = null
                this.scroll.to = null
                this.duration.start = null
                this.duration.current = null
                this.gesture.moving = false
            }
        }

        if (!this.canStart)
        {
            this.scrollOffset = this.gesture.position.x

            this.gesture.position.x -= this.scrollOffset
        }

        if (this.params.onTick)
        {
            this.params.onTick(this.gesture)
        }

        this.params.section.style.transform = `translateX(${this.gesture.position.x}px) translateZ(0)`
    }

    // ACTIONS
    scrollTo(x, smooth = false)
    {
        if (!smooth)
        {
            this.gesture.position.x = -x
        }
        else
        {
            this.scroll.auto = true
            this.scroll.from = this.gesture.position.x

            let _x = Math.ceil(x)
            if (_x < 0)
            {
                _x = 0
            }
            else if (_x > this.params.sectionWidth)
            {
                _x = this.params.sectionWidth
            }

            this.scroll.to = -_x
            this.duration.start = new Date()
        }
    }

    // MOUSE EVENTS
    _onMousedown(event)
    {
        if (this.gesture.moving)
        {
            return
        }

        event.preventDefault()

        this.gesture.moving = true
        this.params.section.classList.add('is-grabbing')

        this.gesture.start.x = event.clientX
        this.gesture.start.y = event.clientY

        this.gesture.previous.x = this.gesture.start.x
        this.gesture.previous.y = this.gesture.start.y

        this.gesture.x = this.gesture.start.x
        this.gesture.y = this.gesture.start.y
    }

    _onMousemove(event)
    {
        if (!this.gesture.moving)
        {
            return
        }

        event.preventDefault()

        this.gesture.x = event.clientX
        this.gesture.y = event.clientY
    }

    _onMouseup()
    {
        if (!this.gesture.moving)
        {
            return
        }

        this.gesture.moving = false
        this.params.section.classList.remove('is-grabbing')
    }

    // TOUCH EVENTS
    _onTouchstart(event)
    {
        if (this.gesture.moving)
        {
            return
        }

        this.gesture.moving = true
        this.params.section.classList.add('is-grabbing')

        this.gesture.start.x = event.touches[0].clientX
        this.gesture.start.y = event.touches[0].clientY

        this.gesture.previous.x = this.gesture.start.x
        this.gesture.previous.y = this.gesture.start.y

        this.gesture.x = this.gesture.start.x
        this.gesture.y = this.gesture.start.y
    }

    _onTouchmove(event)
    {
        if (!this.gesture.moving)
        {
            return
        }

        event.preventDefault()
        this.gesture.x = event.touches[0].clientX
        this.gesture.y = event.touches[0].clientY
    }

    _onTouchend()
    {
        if (!this.gesture.moving)
        {
            return
        }

        this.gesture.moving = false
        this.params.section.classList.remove('is-grabbing')
    }

    // KEYBOARD EVENTS
    _onKeydown()
    {
        if (event.keyCode === 39
            || event.keyCode === 37
            || event.keyCode === 32)
        {
            this.gesture.moving = true
            this.params.section.classList.remove('is-grabbing')

            this.scroll.auto = true
            this.scroll.from = this.gesture.position.x
            this.duration.start = new Date()

            const x = this.params.eventKeyboard.step

            if (event.keyCode === 39
                || event.keyCode === 32)
            {
                if (this.gesture.position.x - x < this.params.sectionLimit)
                {
                    this.scroll.to = this.params.sectionLimit
                }
                else
                {
                    this.scroll.to = this.gesture.position.x - x
                }
            }
            else if (event.keyCode === 37)
            {
                if (this.gesture.position.x + x > 0)
                {
                    this.scroll.to = 0
                }
                else
                {
                    this.scroll.to = this.gesture.position.x + x
                }
            }
        }
    }

    // SCROLL EVENTS
    _onMouseWheel(event)
    {
        // In Safari, IE and in Chrome if 'wheel' isn't defined
        if (this.params.events.includes('horizontal-scroll'))
        {
            this.gesture.speed.y = (event.wheelDeltaX) ? event.wheelDeltaX : 0
        }

        if (this.params.events.includes('vertical-scroll'))
        {
            this.gesture.speed.x = (event.wheelDeltaY) ? event.wheelDeltaY : event.wheelDelta
        }
    }

    _onWheel(event)
    {
        // In Chrome and in Firefox (at least the new one)
        if (this.params.events.includes('horizontal-scroll'))
        {
            this.gesture.speed.y = event.wheelDeltaX || event.deltaX * -1
        }

        if (this.params.events.includes('vertical-scroll'))
        {
            this.gesture.speed.x = event.wheelDeltaY || event.deltaY * -1
        }

        // for our purpose deltamode = 1 means user is on a wheel mouse, not touch pad
        // real meaning: https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent#Delta_modes
        if (this.support.firefox
            && event.deltaMode === 1)
        {
            if (this.params.events.includes('horizontal-scroll'))
            {
                this.gesture.speed.y *= this.params.firefoxMult
            }
            if (this.params.events.includes('vertical-scroll'))
            {
                this.gesture.speed.x *= this.params.firefoxMult
            }
        }

        if (this.params.events.includes('horizontal-scroll'))
        {
            this.gesture.speed.y *= this.params.mouseMult
        }

        if (this.params.events.includes('vertical-scroll'))
        {
            this.gesture.speed.x *= this.params.mouseMult
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/utils/smooth-scrolling.js